import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { css } from '@understory-io/leaf/css';
import { useState, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Image } from '~/ui/image';
import { TextLinkButton } from '~/ui/link';
import { MarkdownHtml, type RenderedMarkdown } from '~/ui/markdown-html';

/** The host's markdown description, rendered by `renderHostDescription`. */
export type HostDescription = {
  html: RenderedMarkdown;
  /** The opening of the description, shown until "Read more" — null when the
   *  description is short enough to show whole. */
  excerptHtml: RenderedMarkdown | null;
};

export type HostCardProps = {
  /** Heading slot — typically a `<Text as="h2">` with the host name. */
  header: ReactNode;
  description?: HostDescription | null;
  coverPhoto?: string | null;
  /** Action slot — buttons or links rendered below the description. */
  children?: ReactNode;
};

/**
 * `sizes` for the cover photo, which spans the card, which in turn spans the
 * full content width of a `<PageCard>` on all three pages that render it. The
 * subtractions are the page inset, the card's `xlarge` padding, and the 4px
 * frame `imageWrapStyles` draws around the photo:
 *
 *   mobile   100vw - 24 - 64 - 8   = 100vw - 96px
 *   tablet   100vw - 48 - 64 - 8   = 100vw - 120px
 *   desktop  1200 (cap) - 64 - 8   = 1128px
 *
 * The cap bites at 1248px, not 1200px — below that the page inset is still
 * shrinking the content box, so the tablet expression stays correct up to
 * there. Measured against production at 1920/1000/767 to confirm.
 */
const COVER_PHOTO_SIZES = [
  '(min-width: 1248px) 1128px',
  '(min-width: 768px) calc(100vw - 120px)',
  'calc(100vw - 96px)',
].join(', ');

export function HostCard({
  header,
  description,
  coverPhoto,
  children,
}: HostCardProps) {
  const [expanded, setExpanded] = useState(false);

  const excerptHtml = description?.excerptHtml ?? null;
  const isTruncatable = excerptHtml !== null;

  return (
    <section className={cardStyles}>
      <Stack gap="xlarge">
        {coverPhoto && (
          <div className={imageWrapStyles}>
            <Image
              src={coverPhoto}
              alt=""
              layout="fullWidth"
              sizes={COVER_PHOTO_SIZES}
              className={imageStyles}
            />
          </div>
        )}
        <Stack gap="xlarge">
          <Stack gap="small">
            <Text as="h2" style="subheadline-small">
              {header}
            </Text>
            {description && (
              <Stack gap="small">
                <MarkdownHtml
                  html={
                    isTruncatable && !expanded ? excerptHtml : description.html
                  }
                />
                {isTruncatable && (
                  <Stack direction="row">
                    <TextLinkButton
                      onClick={() => setExpanded((prev) => !prev)}
                      style="body-large-regular"
                    >
                      {expanded ? (
                        <FormattedMessage
                          defaultMessage="Read less"
                          description="Inline link that collapses an expanded host description"
                        />
                      ) : (
                        <FormattedMessage
                          defaultMessage="Read more"
                          description="Inline link that expands a truncated host description"
                        />
                      )}
                    </TextLinkButton>
                  </Stack>
                )}
              </Stack>
            )}
          </Stack>
          {children && (
            <Stack direction="row" gap="small" wrap align="center">
              {children}
            </Stack>
          )}
        </Stack>
      </Stack>
    </section>
  );
}

const cardStyles = css({
  background: 'surface.background',
  borderRadius: 'xlarge',
  padding: 'xlarge',
});

const imageWrapStyles = css({
  width: 'full',
  height: '[300px]',
  borderRadius: 'large',
  overflow: 'hidden',
  // Inset white "frame" matching the design.
  border: '[4px solid]',
  borderColor: 'surface.raised',
  background: 'surface.neutral',
});

const imageStyles = css({
  width: 'full',
  height: 'full',
  objectFit: 'cover',
});
